import React, { useMemo, useRef, useState } from 'react';
import { Maximize2, Minus, Plus, X } from 'lucide-react';
import clsx from 'clsx';
import type { GraphEdge, GraphNode } from './adoption';

interface KindStyle {
  label: string;
  fill: string;
  ring: string;
  r: number;
  angle: number;
  radius: number;
}

const KIND: Record<string, KindStyle> = {
  company: { label: 'Company', fill: '#18191D', ring: '#18191D', r: 16, angle: 0, radius: 0 },
  department: { label: 'Departments', fill: '#8B5CF6', ring: '#6D28D9', r: 9, angle: 180, radius: 150 },
  person: { label: 'People', fill: '#64748B', ring: '#475569', r: 6, angle: 180, radius: 330 },
  opportunity: { label: 'Opportunities', fill: '#EAB308', ring: '#A16207', r: 6, angle: 270, radius: 300 },
  tool: { label: 'Tools', fill: '#16A34A', ring: '#15803D', r: 8, angle: 0, radius: 290 },
  project: { label: 'Projects', fill: '#2563EB', ring: '#1D4ED8', r: 8, angle: 70, radius: 280 },
  skill: { label: 'Skills', fill: '#0EA5E9', ring: '#0369A1', r: 7, angle: 40, radius: 330 },
  routine: { label: 'Routines', fill: '#06B6D4', ring: '#0E7490', r: 7, angle: 95, radius: 330 },
  workflow: { label: 'Workflows', fill: '#14B8A6', ring: '#0F766E', r: 7, angle: 115, radius: 300 },
  session: { label: 'Sessions', fill: '#F97316', ring: '#C2410C', r: 7, angle: 135, radius: 270 },
};

const TOOL_FILL: Record<string, string> = {
  connected: '#16A34A',
  not_connected: '#F59E0B',
  not_in_catalog: '#A1A1AA',
};

const TOOL_LABEL: Record<string, string> = {
  connected: 'Connected',
  not_connected: 'Used, not connected',
  not_in_catalog: 'Not connectable yet',
};

const HIDDEN_RELS = new Set<string>();

const OUT_PHRASE: Record<string, string> = {
  works_in: 'Works in',
  part_of: 'Part of',
  uses: 'Uses',
  added: 'Added',
  connected_by: 'Connected by',
  created: 'Created',
  needs: 'Needs',
  runs: 'Runs',
  owns: 'Owns',
  for_department: 'For',
  mentions: 'Mentions',
  about_tool: 'About',
  became: 'Became',
  sent_to: 'Sent to',
  member_of: 'Member of',
  built_for: 'Built for',
  raised_at: 'Raised at',
};

const IN_PHRASE: Record<string, string> = {
  works_in: 'Has member',
  part_of: 'Has department',
  uses: 'Used by',
  added: 'Added by',
  connected_by: 'Connected',
  created: 'Created by',
  needs: 'Needed by',
  runs: 'Run by routine',
  owns: 'Owned by',
  for_department: 'Opportunity',
  mentions: 'Mentioned in',
  about_tool: 'Opportunity about it',
  became: 'Came from',
  sent_to: 'Was sent',
  member_of: 'Has member',
  built_for: 'Has project',
  raised_at: 'Has opportunity',
};

const W = 1200;
const H = 820;

const seeded = (seed: number) => {
  let x = seed;
  return () => {
    x = (x * 1664525 + 1013904223) % 4294967296;
    return x / 4294967296;
  };
};

export const KIND_COLOR = (kind: string): string => (KIND[kind] || { fill: '#94A3B8' }).fill;

export const NODE_COLOR = (kind: string, status?: unknown): string => {
  if (kind === 'tool') return TOOL_FILL[String(status || 'not_connected')] || TOOL_FILL.not_connected;
  return KIND_COLOR(kind);
};

const styleOf = (kind: string): KindStyle => KIND[kind] || { label: kind, fill: '#94A3B8', ring: '#64748B', r: 6, angle: 225, radius: 300 };

const fillOf = (n: GraphNode): string => {
  if (n.kind === 'tool') return TOOL_FILL[String(n.props.status || 'not_connected')] || TOOL_FILL.not_connected;
  return styleOf(n.kind).fill;
};

type Point = { x: number; y: number };

const layout = (nodes: GraphNode[], edges: GraphEdge[]): Record<string, Point> => {
  const random = seeded(nodes.length * 7919 + edges.length);
  const cx = W / 2;
  const cy = H / 2;
  const anchor: Record<string, Point> = {};
  const pos: Record<string, Point> = {};
  const vel: Record<string, Point> = {};
  nodes.forEach((n) => {
    const s = styleOf(n.kind);
    const rad = (s.angle * Math.PI) / 180;
    anchor[n.key] = { x: cx + Math.cos(rad) * s.radius, y: cy + Math.sin(rad) * s.radius * 0.8 };
    pos[n.key] = { x: anchor[n.key].x + (random() - 0.5) * 120, y: anchor[n.key].y + (random() - 0.5) * 120 };
    vel[n.key] = { x: 0, y: 0 };
  });
  if (pos.company) pos.company = { x: cx, y: cy };
  const springs = edges.filter((e) => pos[e.src] && pos[e.dst] && !HIDDEN_RELS.has(e.rel));
  const keys = nodes.map((n) => n.key);
  for (let step = 0; step < 240; step += 1) {
    const cool = 1 - step / 260;
    for (let i = 0; i < keys.length; i += 1) {
      for (let j = i + 1; j < keys.length; j += 1) {
        const a = pos[keys[i]];
        const b = pos[keys[j]];
        let dx = a.x - b.x;
        let dy = a.y - b.y;
        let d2 = dx * dx + dy * dy;
        if (d2 < 1) {
          dx = random() - 0.5;
          dy = random() - 0.5;
          d2 = 1;
        }
        if (d2 > 160000) continue;
        const force = 900 / d2;
        vel[keys[i]].x += dx * force;
        vel[keys[i]].y += dy * force;
        vel[keys[j]].x -= dx * force;
        vel[keys[j]].y -= dy * force;
      }
    }
    springs.forEach((e) => {
      const a = pos[e.src];
      const b = pos[e.dst];
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const d = Math.sqrt(dx * dx + dy * dy) || 1;
      const pull = (d - 90) * 0.004;
      vel[e.src].x += (dx / d) * pull * d * 0.05;
      vel[e.src].y += (dy / d) * pull * d * 0.05;
      vel[e.dst].x -= (dx / d) * pull * d * 0.05;
      vel[e.dst].y -= (dy / d) * pull * d * 0.05;
    });
    keys.forEach((k) => {
      vel[k].x += (anchor[k].x - pos[k].x) * 0.03;
      vel[k].y += (anchor[k].y - pos[k].y) * 0.03;
      vel[k].x *= 0.6;
      vel[k].y *= 0.6;
      pos[k].x += Math.max(-30, Math.min(30, vel[k].x)) * cool;
      pos[k].y += Math.max(-30, Math.min(30, vel[k].y)) * cool;
    });
    if (pos.company) pos.company = { x: cx, y: cy };
  }
  const xs = keys.map((k) => pos[k].x);
  const ys = keys.map((k) => pos[k].y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const pad = 70;
  const scale = Math.min((W - pad * 2) / Math.max(1, maxX - minX), (H - pad * 2) / Math.max(1, maxY - minY), 1.6);
  const out: Record<string, Point> = {};
  keys.forEach((k) => {
    out[k] = {
      x: W / 2 + (pos[k].x - (minX + maxX) / 2) * scale,
      y: H / 2 + (pos[k].y - (minY + maxY) / 2) * scale,
    };
  });
  return out;
};

const shortLabel = (text: string): string => {
  if (text.length <= 28) return text;
  return `${text.slice(0, 26)}…`;
};

const detailLines = (node: GraphNode): string[] => {
  const p = node.props;
  const lines: string[] = [];
  if (node.kind === 'tool') lines.push(TOOL_LABEL[String(p.status || 'not_connected')] || '');
  if (node.kind === 'person' && p.role) lines.push(String(p.role));
  if (node.kind === 'person' && p.email) lines.push(String(p.email));
  if (node.kind === 'person' && p.set_up === false) lines.push('Not set up yet');
  if (node.kind === 'opportunity' && p.status) lines.push(String(p.status));
  if (node.kind === 'opportunity' && p.source) lines.push(`From: ${String(p.source)}`);
  if (node.kind === 'project' && p.status) lines.push(String(p.status).replace(/_/g, ' ').toLowerCase());
  if (node.kind === 'session') lines.push(`${String(p.answered ?? 0)} of ${String(p.asked ?? 0)} answered`);
  if ((node.kind === 'routine' || node.kind === 'workflow') && p.running === false) lines.push('Paused or draft');
  if ((node.kind === 'routine' || node.kind === 'workflow') && p.running === true) lines.push('Running');
  if (node.kind === 'skill' && p.published === true) lines.push('Published');
  if (node.kind === 'skill' && p.published === false) lines.push('Draft');
  if (Array.isArray(p.said_by) && p.said_by.length) lines.push((p.said_by as string[]).join(' · '));
  return lines.filter(Boolean);
};

export const GraphMap: React.FC<{ nodes: GraphNode[]; edges: GraphEdge[]; highlight?: Set<string> }> = ({ nodes, edges, highlight }) => {
  const kinds = useMemo(() => Array.from(new Set(nodes.map((n) => n.kind))).filter((k) => k !== 'company'), [nodes]);
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<string>('');
  const [hover, setHover] = useState<string>('');
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState<Point>({ x: 0, y: 0 });
  const drag = useRef<{ x: number; y: number; px: number; py: number } | null>(null);
  const svg = useRef<SVGSVGElement | null>(null);

  const positions = useMemo(() => layout(nodes, edges), [nodes, edges]);
  const byKey = useMemo(() => {
    const map: Record<string, GraphNode> = {};
    nodes.forEach((n) => { map[n.key] = n; });
    return map;
  }, [nodes]);

  const visible = nodes.filter((n) => !hidden.has(n.kind));
  const shown = new Set(visible.map((n) => n.key));
  const lines = edges.filter((e) => shown.has(e.src) && shown.has(e.dst) && !HIDDEN_RELS.has(e.rel));
  const focus = selected || hover;
  const near = new Set<string>();
  if (focus) {
    near.add(focus);
    lines.forEach((e) => {
      if (e.src === focus) near.add(e.dst);
      if (e.dst === focus) near.add(e.src);
    });
  }
  const topic = !focus && !!highlight && highlight.size > 0;
  if (topic && highlight) highlight.forEach((k) => near.add(k));
  const showLabels = visible.length <= 45;

  const toggle = (kind: string) => setHidden((now) => {
    const next = new Set(now);
    if (next.has(kind)) next.delete(kind);
    if (!now.has(kind)) next.add(kind);
    return next;
  });

  const zoomBy = (factor: number) => setZoom((z) => Math.min(4, Math.max(0.5, z * factor)));
  const reset = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  const onDown = (e: React.PointerEvent<SVGSVGElement>) => {
    if ((e.target as Element).closest('[data-node]')) return;
    drag.current = { x: e.clientX, y: e.clientY, px: pan.x, py: pan.y };
    (e.target as Element).setPointerCapture?.(e.pointerId);
  };
  const onMove = (e: React.PointerEvent<SVGSVGElement>) => {
    if (!drag.current || !svg.current) return;
    const box = svg.current.getBoundingClientRect();
    const unit = W / box.width / zoom;
    setPan({ x: drag.current.px + (e.clientX - drag.current.x) * unit, y: drag.current.py + (e.clientY - drag.current.y) * unit });
  };
  const onUp = () => { drag.current = null; };

  const selectedNode = byKey[selected];
  const links = lines.filter((e) => selectedNode && (e.src === selected || e.dst === selected)).map((e) => {
    if (e.src === selected) return { key: `${e.rel}>${e.dst}`, phrase: OUT_PHRASE[e.rel] || e.rel, other: byKey[e.dst] };
    return { key: `${e.rel}<${e.src}`, phrase: IN_PHRASE[e.rel] || e.rel, other: byKey[e.src] };
  });

  const transform = `translate(${W / 2 + pan.x} ${H / 2 + pan.y}) scale(${zoom}) translate(${-W / 2} ${-H / 2})`;

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 px-5 pb-3 sm:px-6" role="group" aria-label="Show or hide kinds of things">
        {kinds.map((k) => {
          const s = styleOf(k);
          const off = hidden.has(k);
          const count = nodes.filter((n) => n.kind === k).length;
          let dotOpacity = 1;
          if (off) dotOpacity = 0.35;
          return (
            <button
              key={k}
              type="button"
              onClick={() => toggle(k)}
              aria-pressed={!off}
              className={clsx('inline-flex min-h-[32px] items-center gap-1.5 rounded-full border px-3 text-[12px] font-semibold transition-colors', {
                'border-line bg-white text-ink hover:border-ink': !off,
                'border-dashed border-line bg-canvas text-ink-soft': off,
              })}
            >
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: s.fill, opacity: dotOpacity }} aria-hidden />
              {s.label} {count}
            </button>
          );
        })}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 px-5 pb-3 text-[11.5px] text-ink-dim sm:px-6">
        {Object.entries(TOOL_LABEL).map(([status, label]) => (
          <span key={status} className="inline-flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full" style={{ background: TOOL_FILL[status] }} aria-hidden />
            Tool: {label}
          </span>
        ))}
        <span>Click a dot to see its links. Drag to move, use + and - to zoom.</span>
      </div>
      <div className="grid border-t border-line-soft lg:grid-cols-[minmax(0,1fr)_300px]">
        <div className="relative bg-canvas">
          <div className="absolute right-3 top-3 z-10 flex flex-col gap-1">
            <button type="button" onClick={() => zoomBy(1.25)} aria-label="Zoom in" className="grid h-9 w-9 place-items-center rounded-lg border border-line bg-white text-ink hover:border-ink"><Plus size={15} aria-hidden /></button>
            <button type="button" onClick={() => zoomBy(0.8)} aria-label="Zoom out" className="grid h-9 w-9 place-items-center rounded-lg border border-line bg-white text-ink hover:border-ink"><Minus size={15} aria-hidden /></button>
            <button type="button" onClick={reset} aria-label="Fit the map" className="grid h-9 w-9 place-items-center rounded-lg border border-line bg-white text-ink hover:border-ink"><Maximize2 size={14} aria-hidden /></button>
          </div>
          <svg
            ref={svg}
            viewBox={`0 0 ${W} ${H}`}
            className="block h-[520px] w-full cursor-grab touch-none select-none active:cursor-grabbing sm:h-[620px]"
            role="img"
            aria-label={`Company knowledge graph with ${nodes.length} things and ${lines.length} links`}
            onPointerDown={onDown}
            onPointerMove={onMove}
            onPointerUp={onUp}
            onPointerLeave={onUp}
            onClick={(e) => { if (!(e.target as Element).closest('[data-node]')) setSelected(''); }}
          >
            <g transform={transform}>
              {lines.map((e) => {
                const a = positions[e.src];
                const b = positions[e.dst];
                if (!a || !b) return null;
                let lit = !!focus && (e.src === focus || e.dst === focus);
                if (topic) lit = near.has(e.src) && near.has(e.dst);
                let stroke = '#CBD5E1';
                let opacity = 0.55;
                let width = 1;
                if ((focus || topic) && !lit) opacity = 0.12;
                if (lit) {
                  stroke = '#18191D';
                  opacity = 0.9;
                  width = 1.8;
                }
                let dash: string | undefined;
                if (e.rel === 'mentions') dash = '4 3';
                const mx = (a.x + b.x) / 2 + (b.y - a.y) * 0.08;
                const my = (a.y + b.y) / 2 - (b.x - a.x) * 0.08;
                return (
                  <path
                    key={`${e.src}|${e.rel}|${e.dst}`}
                    d={`M${a.x},${a.y} Q${mx},${my} ${b.x},${b.y}`}
                    fill="none"
                    stroke={stroke}
                    strokeOpacity={opacity}
                    strokeWidth={width / zoom}
                    strokeDasharray={dash}
                  />
                );
              })}
              {visible.map((n) => {
                const p = positions[n.key];
                if (!p) return null;
                const s = styleOf(n.kind);
                let opacity = 1;
                if ((focus || topic) && !near.has(n.key) && n.kind !== 'company') opacity = 0.2;
                const isSelected = selected === n.key;
                const labelled = n.kind === 'company' || n.kind === 'department' || n.kind === 'tool' || showLabels || near.has(n.key);
                let radius = s.r;
                let ring = '#FFFFFF';
                let ringWidth = 1.5;
                if (isSelected) {
                  radius = s.r + 3;
                  ring = '#18191D';
                  ringWidth = 2.5;
                }
                let fontSize = 11;
                let fontWeight = 500;
                if (n.kind === 'company') fontSize = 15;
                if (n.kind === 'company' || n.kind === 'department') fontWeight = 700;
                return (
                  <g
                    key={n.key}
                    data-node
                    transform={`translate(${p.x} ${p.y})`}
                    opacity={opacity}
                    className="cursor-pointer focus:outline-none"
                    tabIndex={0}
                    role="button"
                    aria-label={`${s.label}: ${n.label}`}
                    aria-pressed={isSelected}
                    onClick={() => setSelected(n.key)}
                    onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') setSelected(n.key); }}
                    onMouseEnter={() => setHover(n.key)}
                    onMouseLeave={() => setHover('')}
                  >
                    <circle r={radius / Math.sqrt(zoom)} fill={fillOf(n)} stroke={ring} strokeWidth={ringWidth / zoom} />
                    {labelled && (
                      <text
                        y={(s.r + 13) / Math.sqrt(zoom)}
                        textAnchor="middle"
                        fontSize={fontSize / Math.sqrt(zoom)}
                        fontWeight={fontWeight}
                        fill="#18191D"
                        stroke="#F7F7F5"
                        strokeWidth={3 / zoom}
                        paintOrder="stroke"
                      >
                        {shortLabel(n.label)}
                      </text>
                    )}
                  </g>
                );
              })}
            </g>
          </svg>
        </div>
        <aside className="border-t border-line-soft bg-white p-5 lg:border-l lg:border-t-0" aria-live="polite">
          {!selectedNode && (
            <div>
              <p className="m-0 text-[13px] font-semibold text-ink">Pick a dot</p>
              <p className="m-0 mt-1 text-[12.5px] leading-[19px] text-ink-dim">
                Each dot is something the platform knows: a person, a tool, an opportunity, a project. Lines show how they connect. Click one to see who created it, what it uses and what it became.
              </p>
            </div>
          )}
          {selectedNode && (
            <div>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="m-0 text-[11px] font-semibold uppercase tracking-[0.1em]" style={{ color: styleOf(selectedNode.kind).ring }}>{styleOf(selectedNode.kind).label}</p>
                  <p className="m-0 mt-1 break-words text-[14.5px] font-bold leading-[20px] text-ink">{selectedNode.label}</p>
                </div>
                <button type="button" onClick={() => setSelected('')} aria-label="Close" className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-ink-dim hover:bg-canvas-inset hover:text-ink"><X size={15} aria-hidden /></button>
              </div>
              {detailLines(selectedNode).map((line) => <p key={line} className="m-0 mt-1 text-[12.5px] text-ink-muted">{line}</p>)}
              <p className="m-0 mt-4 text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft">Links ({links.length})</p>
              {links.length === 0 && <p className="m-0 mt-1 text-[12.5px] text-ink-dim">Nothing links to this yet.</p>}
              <ul className="m-0 mt-1.5 max-h-[400px] list-none space-y-1 overflow-y-auto p-0">
                {links.map((l) => (
                  <li key={l.key}>
                    <button type="button" onClick={() => l.other && setSelected(l.other.key)} className="flex w-full items-start gap-2 rounded-lg px-2 py-1.5 text-left text-[12.5px] hover:bg-canvas">
                      <span className="mt-1 h-2 w-2 shrink-0 rounded-full" style={{ background: l.other && fillOf(l.other) }} aria-hidden />
                      <span className="min-w-0">
                        <span className="text-ink-dim">{l.phrase} </span>
                        <span className="font-semibold text-ink">{l.other?.label}</span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
};

export default GraphMap;
